import { __rest } from "tslib";
import { BaseSocialLogin } from './base';
import { createUserCancelledError, inferUserCancelledError } from './errors';
const normalizeOAuthParamName = (value, fallback) => {
    if (typeof value !== 'string') {
        return fallback;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : fallback;
};
/**
 * OAuth2 Social Login Manager
 * Supports multiple OAuth2 provider configurations
 */
export class OAuth2SocialLogin extends BaseSocialLogin {
    constructor() {
        super();
        this.providers = new Map();
        this.TOKENS_KEY_PREFIX = 'capgo_social_login_oauth2_tokens_';
        this.STATE_PREFIX = 'capgo_social_login_oauth2_state_';
        this.CONFIG_KEY_PREFIX = 'capgo_social_login_oauth2_config_';
        // Restore configurations from localStorage for popup window context
        this.restoreConfigurationsFromStorage();
    }
    normalizeScopeValue(scope) {
        if (!scope)
            return '';
        if (typeof scope === 'string')
            return scope;
        if (Array.isArray(scope))
            return scope.filter(Boolean).join(' ');
        return '';
    }
    normalizeConfig(providerId, config) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const appId = (_a = config.appId) !== null && _a !== void 0 ? _a : config.clientId;
        const authorizationBaseUrl = (_b = config.authorizationBaseUrl) !== null && _b !== void 0 ? _b : config.authorizationEndpoint;
        const accessTokenEndpoint = (_c = config.accessTokenEndpoint) !== null && _c !== void 0 ? _c : config.tokenEndpoint;
        const logoutUrl = (_d = config.logoutUrl) !== null && _d !== void 0 ? _d : config.endSessionEndpoint;
        const scopeSource = (_e = config.scope) !== null && _e !== void 0 ? _e : config.scopes;
        if (!appId) {
            throw new Error(`OAuth2 provider '${providerId}' requires appId (or clientId).`);
        }
        if (!config.redirectUrl) {
            throw new Error(`OAuth2 provider '${providerId}' requires redirectUrl.`);
        }
        if (!authorizationBaseUrl && !config.issuerUrl) {
            throw new Error(`OAuth2 provider '${providerId}' requires authorizationBaseUrl (or authorizationEndpoint) or issuerUrl.`);
        }
        return {
            appId,
            clientSecret: config.clientSecret,
            clientIdParamName: normalizeOAuthParamName(config.clientIdParamName, 'client_id'),
            clientSecretParamName: normalizeOAuthParamName(config.clientSecretParamName, 'client_secret'),
            issuerUrl: config.issuerUrl,
            authorizationBaseUrl,
            accessTokenEndpoint,
            redirectUrl: config.redirectUrl,
            resourceUrl: config.resourceUrl,
            responseType: ((_f = config.responseType) !== null && _f !== void 0 ? _f : 'code'),
            pkceEnabled: (_g = config.pkceEnabled) !== null && _g !== void 0 ? _g : true,
            scope: this.normalizeScopeValue(scopeSource),
            additionalParameters: config.additionalParameters,
            loginHint: config.loginHint,
            prompt: config.prompt,
            additionalTokenParameters: config.additionalTokenParameters,
            additionalResourceHeaders: config.additionalResourceHeaders,
            logoutUrl,
            postLogoutRedirectUrl: config.postLogoutRedirectUrl,
            additionalLogoutParameters: config.additionalLogoutParameters,
            logsEnabled: (_h = config.logsEnabled) !== null && _h !== void 0 ? _h : false,
        };
    }
    async ensureDiscovered(providerId) {
        const config = this.providers.get(providerId);
        if (!(config === null || config === void 0 ? void 0 : config.issuerUrl))
            return;
        // Resolve endpoints lazily.
        if (config.authorizationBaseUrl && config.accessTokenEndpoint)
            return;
        const issuer = config.issuerUrl.replace(/\/+$/, '');
        const discoveryUrl = `${issuer}/.well-known/openid-configuration`;
        const resp = await fetch(discoveryUrl);
        if (!resp.ok) {
            const text = await resp.text().catch(() => '');
            throw new Error(`OAuth2 discovery failed (${resp.status}): ${text || discoveryUrl}`);
        }
        const payload = (await resp.json());
        const authorizationEndpoint = payload['authorization_endpoint'];
        const tokenEndpoint = payload['token_endpoint'];
        const endSessionEndpoint = payload['end_session_endpoint'];
        if (!config.authorizationBaseUrl && typeof authorizationEndpoint === 'string') {
            config.authorizationBaseUrl = authorizationEndpoint;
        }
        if (!config.accessTokenEndpoint && typeof tokenEndpoint === 'string') {
            config.accessTokenEndpoint = tokenEndpoint;
        }
        if (!config.logoutUrl && typeof endSessionEndpoint === 'string') {
            config.logoutUrl = endSessionEndpoint;
        }
        if (config.logsEnabled) {
            console.log(`[OAuth2:${providerId}] Discovery resolved`, {
                authorizationBaseUrl: config.authorizationBaseUrl,
                accessTokenEndpoint: config.accessTokenEndpoint,
                logoutUrl: config.logoutUrl,
            });
        }
        // Persist updated configuration after discovery
        this.persistConfiguration(providerId, config);
    }
    /**
     * Initialize multiple OAuth2 providers
     */
    async initializeProviders(configs) {
        for (const [providerId, config] of Object.entries(configs)) {
            const internalConfig = this.normalizeConfig(providerId, config);
            this.providers.set(providerId, internalConfig);
            if (internalConfig.logsEnabled) {
                console.log(`[OAuth2:${providerId}] Initialized with config:`, {
                    appId: internalConfig.appId,
                    issuerUrl: internalConfig.issuerUrl,
                    authorizationBaseUrl: internalConfig.authorizationBaseUrl,
                    redirectUrl: internalConfig.redirectUrl,
                    responseType: internalConfig.responseType,
                    pkceEnabled: internalConfig.pkceEnabled,
                });
            }
            // Pre-resolve discovery on web if issuerUrl is provided.
            await this.ensureDiscovered(providerId);
            // Persist configuration to localStorage so it's available in popup window context
            this.persistConfiguration(providerId, internalConfig);
        }
    }
    normalizeStoredConfig(config) {
        return Object.assign(Object.assign({}, config), { clientIdParamName: normalizeOAuthParamName(config.clientIdParamName, 'client_id'), clientSecretParamName: normalizeOAuthParamName(config.clientSecretParamName, 'client_secret') });
    }
    getProvider(providerId) {
        const config = this.providers.get(providerId);
        if (!config) {
            throw new Error(`OAuth2 provider '${providerId}' not configured. Call initialize() first.`);
        }
        // Mutate in place so later discovery writes are visible to the same config object.
        config.clientIdParamName = normalizeOAuthParamName(config.clientIdParamName, 'client_id');
        config.clientSecretParamName = normalizeOAuthParamName(config.clientSecretParamName, 'client_secret');
        return config;
    }
    getTokensKey(providerId) {
        return `${this.TOKENS_KEY_PREFIX}${providerId}`;
    }
    getConfigKey(providerId) {
        return `${this.CONFIG_KEY_PREFIX}${providerId}`;
    }
    persistConfiguration(providerId, config) {
        try {
            localStorage.setItem(this.getConfigKey(providerId), JSON.stringify(config));
        }
        catch (err) {
            console.warn(`Failed to persist OAuth2 configuration for provider '${providerId}'`, err);
        }
    }
    restoreConfigurationsFromStorage() {
        // Restore all provider configurations from localStorage
        // This is needed for popup window context where the parent's in-memory state is not available
        const keys = Object.keys(localStorage);
        for (const key of keys) {
            if (key.startsWith(this.CONFIG_KEY_PREFIX)) {
                const providerId = key.substring(this.CONFIG_KEY_PREFIX.length);
                try {
                    const raw = localStorage.getItem(key);
                    if (raw) {
                        const config = this.normalizeStoredConfig(JSON.parse(raw));
                        this.providers.set(providerId, config);
                    }
                }
                catch (err) {
                    console.warn(`Failed to restore OAuth2 configuration for provider '${providerId}'`, err);
                }
            }
        }
    }
    async login(options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const { providerId } = options;
        const config = this.getProvider(providerId);
        await this.ensureDiscovered(providerId);
        const redirectUri = (_a = options.redirectUrl) !== null && _a !== void 0 ? _a : config.redirectUrl;
        const scope = this.normalizeScopeValue((_c = (_b = options.scope) !== null && _b !== void 0 ? _b : options.scopes) !== null && _c !== void 0 ? _c : config.scope);
        const state = (_d = options.state) !== null && _d !== void 0 ? _d : this.generateState();
        const codeVerifier = (_e = options.codeVerifier) !== null && _e !== void 0 ? _e : this.generateCodeVerifier();
        // Build authorization URL
        const params = new URLSearchParams({
            response_type: config.responseType,
            [config.clientIdParamName]: config.appId,
            redirect_uri: redirectUri,
            state,
        });
        if (scope) {
            params.set('scope', scope);
        }
        // Convenience OIDC options
        const mergedAdditionalParams = Object.assign(Object.assign({}, ((_f = config.additionalParameters) !== null && _f !== void 0 ? _f : {})), ((_g = options.additionalParameters) !== null && _g !== void 0 ? _g : {}));
        const loginHint = (_h = options.loginHint) !== null && _h !== void 0 ? _h : config.loginHint;
        const prompt = (_j = options.prompt) !== null && _j !== void 0 ? _j : config.prompt;
        if (loginHint && !('login_hint' in mergedAdditionalParams)) {
            mergedAdditionalParams.login_hint = loginHint;
        }
        if (prompt && !('prompt' in mergedAdditionalParams)) {
            mergedAdditionalParams.prompt = prompt;
        }
        // Add PKCE for code flow
        if (config.responseType === 'code' && config.pkceEnabled) {
            const codeChallenge = await this.generateCodeChallenge(codeVerifier);
            params.set('code_challenge', codeChallenge);
            params.set('code_challenge_method', 'S256');
        }
        // Add merged additional parameters
        for (const [key, value] of Object.entries(mergedAdditionalParams)) {
            if (value !== undefined) {
                params.set(key, value);
            }
        }
        // Store pending login state
        this.persistPendingLogin(state, {
            providerId,
            codeVerifier,
            redirectUri,
            scope,
        });
        localStorage.setItem(BaseSocialLogin.OAUTH_STATE_KEY, JSON.stringify({ provider: 'oauth2', providerId, state }));
        if (!config.authorizationBaseUrl) {
            throw new Error(`OAuth2 provider '${providerId}' is missing authorizationBaseUrl (discovery may have failed).`);
        }
        const authUrl = `${config.authorizationBaseUrl}?${params.toString()}`;
        if (config.logsEnabled) {
            console.log(`[OAuth2:${providerId}] Opening authorization URL:`, authUrl);
        }
        if (options.flow === 'redirect') {
            // Trigger a full-page redirect. The promise will not resolve because the page navigates away.
            window.location.assign(authUrl);
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            return new Promise(() => { });
        }
        // Open popup window
        const width = 500;
        const height = 650;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(authUrl, 'OAuth2Login', `width=${width},height=${height},left=${left},top=${top},popup=1`);
        return new Promise((resolve, reject) => {
            if (!popup) {
                reject(new Error('Unable to open login window. Please allow popups.'));
                return;
            }
            // Use BroadcastChannel for cross-origin communication (works when postMessage doesn't)
            const channelName = `oauth2_${state}`;
            let broadcastChannel = null;
            try {
                broadcastChannel = new BroadcastChannel(channelName);
            }
            catch (_a) {
                // BroadcastChannel not supported, fall back to postMessage only
                if (config.logsEnabled) {
                    console.log(`[OAuth2:${providerId}] BroadcastChannel not supported, using postMessage only`);
                }
            }
            const cleanup = (messageHandler, timeoutHandle, intervalHandle) => {
                window.removeEventListener('message', messageHandler);
                clearTimeout(timeoutHandle);
                clearInterval(intervalHandle);
                if (broadcastChannel) {
                    broadcastChannel.close();
                }
            };
            const handleOAuthMessage = (data) => {
                if ((data === null || data === void 0 ? void 0 : data.type) === 'oauth-response') {
                    if ((data === null || data === void 0 ? void 0 : data.provider) && data.provider !== 'oauth2') {
                        return false;
                    }
                    // Check providerId matches if present
                    if ((data === null || data === void 0 ? void 0 : data.providerId) && data.providerId !== providerId) {
                        return false;
                    }
                    cleanup(messageHandler, timeoutHandle, popupClosedInterval);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const _a = data, { provider: _ignoredProvider, type: _ignoredType } = _a, payload = __rest(_a, ["provider", "type"]);
                    resolve({
                        provider: 'oauth2',
                        result: payload,
                    });
                    return true;
                }
                else if ((data === null || data === void 0 ? void 0 : data.type) === 'oauth-error') {
                    if ((data === null || data === void 0 ? void 0 : data.provider) && data.provider !== 'oauth2') {
                        return false;
                    }
                    cleanup(messageHandler, timeoutHandle, popupClosedInterval);
                    reject(inferUserCancelledError(data.error || 'OAuth2 login was cancelled.'));
                    return true;
                }
                return false;
            };
            // Listen for BroadcastChannel messages
            if (broadcastChannel) {
                broadcastChannel.onmessage = (event) => {
                    handleOAuthMessage(event.data);
                };
            }
            const messageHandler = (event) => {
                if (event.origin !== window.location.origin) {
                    return;
                }
                handleOAuthMessage(event.data);
            };
            window.addEventListener('message', messageHandler);
            const timeoutHandle = window.setTimeout(() => {
                cleanup(messageHandler, timeoutHandle, popupClosedInterval);
                try {
                    popup.close();
                }
                catch (_a) {
                    // Ignore cross-origin errors when closing
                }
                reject(new Error('OAuth2 login timed out.'));
            }, 300000);
            const popupClosedInterval = window.setInterval(() => {
                try {
                    // Check if popup is closed - this may throw cross-origin errors for some providers
                    if (popup.closed) {
                        cleanup(messageHandler, timeoutHandle, popupClosedInterval);
                        reject(createUserCancelledError('OAuth2 login window was closed.'));
                    }
                }
                catch (_a) {
                    // Cross-origin error when checking popup.closed - this happens when the popup
                    // navigates to a third-party OAuth provider with strict security settings.
                    // We can't detect if the window was closed, so we just rely on the timeout
                    // and message handlers. Clear the interval to avoid repeated errors.
                    clearInterval(popupClosedInterval);
                    if (config.logsEnabled) {
                        console.log(`[OAuth2:${providerId}] Cannot check popup.closed due to cross-origin restrictions. ` +
                            'Relying on message handlers and timeout.');
                    }
                }
            }, 1000);
        });
    }
    async logout(providerId) {
        await this.ensureDiscovered(providerId);
        const config = this.providers.get(providerId);
        const stored = this.getStoredTokens(providerId);
        localStorage.removeItem(this.getTokensKey(providerId));
        // If logout URL is configured, build an end-session URL (OIDC) when possible.
        if (config === null || config === void 0 ? void 0 : config.logoutUrl) {
            try {
                const url = new URL(config.logoutUrl);
                if (stored === null || stored === void 0 ? void 0 : stored.idToken) {
                    url.searchParams.set('id_token_hint', stored.idToken);
                }
                const postLogout = config.postLogoutRedirectUrl;
                if (postLogout) {
                    url.searchParams.set('post_logout_redirect_uri', postLogout);
                }
                if (config.additionalLogoutParameters) {
                    for (const [k, v] of Object.entries(config.additionalLogoutParameters)) {
                        url.searchParams.set(k, v);
                    }
                }
                window.open(url.toString(), '_blank');
            }
            catch (_a) {
                window.open(config.logoutUrl, '_blank');
            }
        }
    }
    async isLoggedIn(providerId) {
        const tokens = this.getStoredTokens(providerId);
        if (!tokens) {
            return { isLoggedIn: false };
        }
        const isValid = tokens.expiresAt > Date.now();
        if (!isValid) {
            localStorage.removeItem(this.getTokensKey(providerId));
        }
        return { isLoggedIn: isValid };
    }
    async getAuthorizationCode(providerId) {
        const tokens = this.getStoredTokens(providerId);
        if (!tokens) {
            throw new Error(`OAuth2 access token is not available for provider '${providerId}'.`);
        }
        return {
            accessToken: tokens.accessToken,
            jwt: tokens.idToken,
        };
    }
    async refresh(providerId) {
        await this.refreshToken(providerId);
    }
    async refreshToken(providerId, refreshToken, additionalParameters) {
        var _a, _b, _c, _d, _e, _f;
        await this.ensureDiscovered(providerId);
        const config = this.getProvider(providerId);
        const stored = this.getStoredTokens(providerId);
        const effectiveRefreshToken = refreshToken !== null && refreshToken !== void 0 ? refreshToken : stored === null || stored === void 0 ? void 0 : stored.refreshToken;
        if (!effectiveRefreshToken) {
            throw new Error(`No OAuth2 refresh token is available for provider '${providerId}'. Include offline_access scope to receive one.`);
        }
        if (!config.accessTokenEndpoint) {
            throw new Error(`No accessTokenEndpoint configured for provider '${providerId}'.`);
        }
        const tokenResponse = await this.refreshWithRefreshToken(providerId, effectiveRefreshToken, additionalParameters);
        const expiresAt = tokenResponse.expires_in ? Date.now() + tokenResponse.expires_in * 1000 : Date.now() + 3600000;
        const scopeArray = (_c = (_b = (_a = tokenResponse.scope) === null || _a === void 0 ? void 0 : _a.split(' ').filter(Boolean)) !== null && _b !== void 0 ? _b : stored === null || stored === void 0 ? void 0 : stored.scope) !== null && _c !== void 0 ? _c : [];
        // Fetch resource data if configured
        let resourceData = null;
        if (config.resourceUrl) {
            resourceData = await this.fetchResource(providerId, tokenResponse.access_token);
        }
        const nextRefreshToken = (_d = tokenResponse.refresh_token) !== null && _d !== void 0 ? _d : effectiveRefreshToken;
        this.persistTokens(providerId, {
            accessToken: tokenResponse.access_token,
            refreshToken: nextRefreshToken,
            idToken: tokenResponse.id_token,
            expiresAt,
            scope: scopeArray,
            tokenType: tokenResponse.token_type,
        });
        return {
            providerId,
            accessToken: {
                token: tokenResponse.access_token,
                tokenType: tokenResponse.token_type,
                expires: new Date(expiresAt).toISOString(),
                refreshToken: nextRefreshToken,
            },
            idToken: (_e = tokenResponse.id_token) !== null && _e !== void 0 ? _e : null,
            refreshToken: nextRefreshToken !== null && nextRefreshToken !== void 0 ? nextRefreshToken : null,
            resourceData,
            scope: scopeArray,
            tokenType: tokenResponse.token_type,
            expiresIn: (_f = tokenResponse.expires_in) !== null && _f !== void 0 ? _f : null,
        };
    }
    async handleOAuthRedirect(url, expectedState) {
        var _a, _b, _c, _d, _e;
        // Check both query params and hash fragment
        const params = new URLSearchParams(url.search);
        const hashParams = new URLSearchParams(url.hash.slice(1));
        // Merge params, hash takes priority (for implicit flow)
        hashParams.forEach((value, key) => {
            params.set(key, value);
        });
        const stateFromUrl = expectedState !== null && expectedState !== void 0 ? expectedState : params.get('state');
        if (!stateFromUrl) {
            return null;
        }
        const pending = this.consumePendingLogin(stateFromUrl);
        if (!pending) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            return { error: 'OAuth2 login session expired or state mismatch.' };
        }
        const { providerId } = pending;
        await this.ensureDiscovered(providerId);
        const config = this.providers.get(providerId);
        if (!config) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            return { error: `OAuth2 provider '${providerId}' configuration not found.` };
        }
        const error = params.get('error');
        if (error) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            return { error: params.get('error_description') || error };
        }
        try {
            let tokenResponse;
            // Check response type
            if (params.has('code')) {
                // Authorization code flow
                const code = params.get('code');
                if (!code) {
                    localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
                    return { error: 'OAuth2 authorization code missing from redirect.' };
                }
                tokenResponse = await this.exchangeAuthorizationCode(providerId, code, pending);
            }
            else if (params.has('access_token')) {
                // Implicit flow
                tokenResponse = {
                    access_token: params.get('access_token'),
                    token_type: params.get('token_type') || 'bearer',
                    expires_in: params.has('expires_in') ? parseInt(params.get('expires_in'), 10) : undefined,
                    scope: params.get('scope') || undefined,
                    id_token: params.get('id_token') || undefined,
                };
            }
            else {
                localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
                return { error: 'No authorization code or access token in redirect.' };
            }
            const expiresAt = tokenResponse.expires_in ? Date.now() + tokenResponse.expires_in * 1000 : Date.now() + 3600000;
            const scopeArray = (_b = (_a = tokenResponse.scope) === null || _a === void 0 ? void 0 : _a.split(' ').filter(Boolean)) !== null && _b !== void 0 ? _b : [];
            // Fetch resource data if configured
            let resourceData = null;
            if (config.resourceUrl) {
                resourceData = await this.fetchResource(providerId, tokenResponse.access_token);
            }
            this.persistTokens(providerId, {
                accessToken: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token,
                idToken: tokenResponse.id_token,
                expiresAt,
                scope: scopeArray,
                tokenType: tokenResponse.token_type,
            });
            return {
                provider: 'oauth2',
                result: {
                    providerId,
                    accessToken: {
                        token: tokenResponse.access_token,
                        tokenType: tokenResponse.token_type,
                        expires: new Date(expiresAt).toISOString(),
                        refreshToken: tokenResponse.refresh_token,
                    },
                    idToken: (_c = tokenResponse.id_token) !== null && _c !== void 0 ? _c : null,
                    refreshToken: (_d = tokenResponse.refresh_token) !== null && _d !== void 0 ? _d : null,
                    resourceData,
                    scope: scopeArray,
                    tokenType: tokenResponse.token_type,
                    expiresIn: (_e = tokenResponse.expires_in) !== null && _e !== void 0 ? _e : null,
                },
            };
        }
        catch (err) {
            if (err instanceof Error) {
                return { error: err.message };
            }
            return { error: 'OAuth2 login failed unexpectedly.' };
        }
        finally {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
        }
    }
    async exchangeAuthorizationCode(providerId, code, pending) {
        const config = this.getProvider(providerId);
        if (!config.accessTokenEndpoint) {
            throw new Error(`No accessTokenEndpoint configured for provider '${providerId}'.`);
        }
        const params = new URLSearchParams({
            grant_type: 'authorization_code',
            [config.clientIdParamName]: config.appId,
            code,
            redirect_uri: pending.redirectUri,
        });
        if (config.pkceEnabled) {
            params.set('code_verifier', pending.codeVerifier);
        }
        if (config.clientSecret) {
            params.set(config.clientSecretParamName, config.clientSecret);
        }
        if (config.additionalTokenParameters) {
            for (const [k, v] of Object.entries(config.additionalTokenParameters)) {
                params.set(k, v);
            }
        }
        if (config.logsEnabled) {
            console.log(`[OAuth2:${providerId}] Exchanging code at:`, config.accessTokenEndpoint);
        }
        const response = await fetch(config.accessTokenEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params.toString(),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`OAuth2 token exchange failed (${response.status}): ${text}`);
        }
        return (await response.json());
    }
    async refreshWithRefreshToken(providerId, refreshToken, additionalParameters) {
        const config = this.getProvider(providerId);
        if (!config.accessTokenEndpoint) {
            throw new Error(`No accessTokenEndpoint configured for provider '${providerId}'.`);
        }
        const params = new URLSearchParams({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            [config.clientIdParamName]: config.appId,
        });
        if (config.clientSecret) {
            params.set(config.clientSecretParamName, config.clientSecret);
        }
        if (config.additionalTokenParameters) {
            for (const [k, v] of Object.entries(config.additionalTokenParameters)) {
                params.set(k, v);
            }
        }
        if (additionalParameters) {
            for (const [k, v] of Object.entries(additionalParameters)) {
                params.set(k, v);
            }
        }
        const response = await fetch(config.accessTokenEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params.toString(),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`OAuth2 refresh failed (${response.status}): ${text}`);
        }
        return (await response.json());
    }
    async fetchResource(providerId, accessToken) {
        const config = this.getProvider(providerId);
        if (!config.resourceUrl) {
            throw new Error(`No resourceUrl configured for provider '${providerId}'.`);
        }
        const headers = {
            Authorization: `Bearer ${accessToken}`,
        };
        if (config.additionalResourceHeaders) {
            Object.assign(headers, config.additionalResourceHeaders);
        }
        const response = await fetch(config.resourceUrl, {
            headers,
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(`Unable to fetch OAuth2 resource (${response.status}): ${text}`);
        }
        return (await response.json());
    }
    persistTokens(providerId, tokens) {
        localStorage.setItem(this.getTokensKey(providerId), JSON.stringify(tokens));
    }
    getStoredTokens(providerId) {
        const raw = localStorage.getItem(this.getTokensKey(providerId));
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw);
        }
        catch (err) {
            console.warn(`Failed to parse stored OAuth2 tokens for provider '${providerId}'`, err);
            return null;
        }
    }
    persistPendingLogin(state, payload) {
        localStorage.setItem(`${this.STATE_PREFIX}${state}`, JSON.stringify(payload));
    }
    consumePendingLogin(state) {
        const key = `${this.STATE_PREFIX}${state}`;
        const raw = localStorage.getItem(key);
        localStorage.removeItem(key);
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw);
        }
        catch (err) {
            console.warn('Failed to parse pending OAuth2 login payload', err);
            return null;
        }
    }
    generateState() {
        return [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, '0')).join('');
    }
    generateCodeVerifier() {
        const array = new Uint8Array(64);
        crypto.getRandomValues(array);
        return Array.from(array)
            .map((b) => 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-._~'[b % 66])
            .join('');
    }
    async generateCodeChallenge(codeVerifier) {
        const encoder = new TextEncoder();
        const data = encoder.encode(codeVerifier);
        const digest = await crypto.subtle.digest('SHA-256', data);
        return this.base64UrlEncode(new Uint8Array(digest));
    }
    base64UrlEncode(buffer) {
        let binary = '';
        buffer.forEach((b) => (binary += String.fromCharCode(b)));
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    decodeIdToken(idToken) {
        const parts = idToken.split('.');
        if (parts.length < 2) {
            throw new Error('Invalid JWT: missing parts');
        }
        const payload = parts[1];
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
        const json = atob(padded);
        return JSON.parse(json);
    }
    getAccessTokenExpirationDate(providerId) {
        const tokens = this.getStoredTokens(providerId);
        if (!(tokens === null || tokens === void 0 ? void 0 : tokens.expiresAt))
            return { expirationDate: null };
        return { expirationDate: new Date(tokens.expiresAt).toISOString() };
    }
    isAccessTokenAvailable(providerId) {
        const tokens = this.getStoredTokens(providerId);
        return { isAvailable: !!(tokens === null || tokens === void 0 ? void 0 : tokens.accessToken) };
    }
    isAccessTokenExpired(providerId) {
        const tokens = this.getStoredTokens(providerId);
        if (!(tokens === null || tokens === void 0 ? void 0 : tokens.expiresAt))
            return { isExpired: true };
        return { isExpired: tokens.expiresAt <= Date.now() };
    }
    isRefreshTokenAvailable(providerId) {
        const tokens = this.getStoredTokens(providerId);
        return { isAvailable: !!(tokens === null || tokens === void 0 ? void 0 : tokens.refreshToken) };
    }
}
//# sourceMappingURL=oauth2-provider.js.map